<template>
  <v-dialog
    v-model="dialogVisible"
    max-width="900px"
    persistent
    scrollable
  >
    <v-card>
      <v-card-title class="primary white--text">
        <v-icon left color="white">mdi-image-multiple</v-icon>
        {{ $t('item.title.batch_upload_images') }}
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pt-6">
        <!-- File Input Section -->
        <v-file-input
          v-model="selectedFiles"
          :label="$t('item.label.select_images')"
          accept="image/*"
          multiple
          :counter="true"
          :show-size="true"
          :rules="fileRules"
          :disabled="uploading"
          prepend-icon="mdi-camera-multiple"
          @change="handleFileChange"
        >
          <template v-slot:message="{ message }">
            <div class="text-caption">
              {{ $t('item.label.batch_upload_filename_hint') }}
            </div>
            <div class="text-caption grey--text mt-1">
              {{ $t('item.label.max_images') }}
            </div>
          </template>
        </v-file-input>

        <!-- Image Previews -->
        <v-card
          v-if="imagePreviews.length > 0"
          outlined
          class="mt-4"
        >
          <v-card-title class="text-subtitle-1">
            {{ $t('item.label.image_previews') }}
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-row>
              <v-col
                v-for="(preview, index) in imagePreviews"
                :key="index"
                cols="12"
                sm="6"
                md="4"
              >
                <v-card outlined>
                  <v-img
                    :src="preview.url"
                    height="150"
                    contain
                  >
                    <template v-slot:placeholder>
                      <v-row
                        class="fill-height ma-0"
                        align="center"
                        justify="center"
                      >
                        <v-progress-circular
                          indeterminate
                          color="grey lighten-5"
                        ></v-progress-circular>
                      </v-row>
                    </template>
                  </v-img>
                  <v-card-text class="py-2">
                    <div class="text-caption text-truncate" :title="preview.name">
                      {{ preview.name }}
                    </div>
                    <div class="text-caption grey--text">
                      {{ formatFileSize(preview.size) }}
                    </div>
                  </v-card-text>
                  <v-card-actions class="pt-0">
                    <v-btn
                      small
                      text
                      color="error"
                      @click="removeImage(index)"
                      :disabled="uploading"
                    >
                      <v-icon small left>mdi-delete</v-icon>
                      {{ $t('button.remove') }}
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Upload Results -->
        <v-card
          v-if="uploadResults"
          outlined
          class="mt-4"
        >
          <v-card-title class="text-subtitle-1">
            {{ $t('item.label.upload_results') }}
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="4">
                <v-card color="success" dark>
                  <v-card-text>
                    <div class="text-h6">{{ uploadResults.matched_count || 0 }}</div>
                    <div class="text-caption">{{ $t('item.label.matched_count') }}</div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" md="4">
                <v-card color="warning" dark>
                  <v-card-text>
                    <div class="text-h6">{{ uploadResults.skipped_count || 0 }}</div>
                    <div class="text-caption">{{ $t('item.label.skipped_count') }}</div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" md="4">
                <v-card color="error" dark>
                  <v-card-text>
                    <div class="text-h6">{{ uploadResults.unmatched_files ? uploadResults.unmatched_files.length : 0 }}</div>
                    <div class="text-caption">{{ $t('item.label.unmatched_files') }}</div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <!-- Unmatched Files List -->
            <v-alert
              v-if="uploadResults.unmatched_files && uploadResults.unmatched_files.length > 0"
              type="warning"
              text
              dense
              class="mt-4"
            >
              <div class="text-subtitle-2 mb-2">{{ $t('item.label.unmatched_files') }}:</div>
              <ul class="mb-0 pl-4">
                <li v-for="(filename, idx) in uploadResults.unmatched_files" :key="idx">
                  {{ filename }}
                </li>
              </ul>
            </v-alert>
          </v-card-text>
        </v-card>

        <!-- Error Alert -->
        <v-alert
          v-if="errorMessage"
          type="error"
          text
          dense
          class="mt-4"
        >
          {{ errorMessage }}
        </v-alert>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn
          color="grey"
          text
          large
          @click="handleClose"
          :disabled="uploading"
        >
          {{ $t('button.close') }}
        </v-btn>
        <v-btn
          color="primary"
          large
          :disabled="!canUpload || uploading"
          :loading="uploading"
          @click="handleUpload"
        >
          <v-icon left>mdi-upload</v-icon>
          {{ $t('button.upload') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'BatchImageUpload',
  props: {
    display: {
      type: Boolean,
      required: true
    }
  },
  data () {
    return {
      selectedFiles: [],
      imagePreviews: [],
      uploading: false,
      uploadResults: null,
      errorMessage: null
    }
  },
  computed: {
    dialogVisible: {
      get () {
        return this.display
      },
      set (value) {
        if (!value) {
          this.$emit('close')
        }
      }
    },
    canUpload () {
      return this.imagePreviews.length > 0 && this.imagePreviews.length <= 10
    },
    fileRules () {
      return [
        (files) => {
          if (!files || files.length === 0) return true
          if (files.length > 10) {
            return this.$t('item.label.max_images')
          }
          const invalidFiles = Array.from(files).filter(file => !file.type.startsWith('image/'))
          if (invalidFiles.length > 0) {
            return this.$t('validation.image_files_only') || 'Only image files are allowed'
          }
          return true
        }
      ]
    }
  },
  watch: {
    display (val) {
      if (val) {
        this.reset()
      }
    }
  },
  methods: {
    reset () {
      this.selectedFiles = []
      this.imagePreviews = []
      this.uploading = false
      this.uploadResults = null
      this.errorMessage = null
    },
    handleFileChange (files) {
      this.errorMessage = null
      this.uploadResults = null

      if (!files || files.length === 0) {
        this.imagePreviews = []
        return
      }

      if (files.length > 10) {
        this.errorMessage = this.$t('item.label.max_images')
        this.selectedFiles = []
        this.imagePreviews = []
        return
      }

      const imageFiles = Array.from(files).filter(file => file.type.startsWith('image/'))
      if (imageFiles.length !== files.length) {
        this.errorMessage = this.$t('validation.image_files_only') || 'Only image files are allowed'
      }

      this.imagePreviews = imageFiles.map(file => ({
        file,
        name: file.name,
        size: file.size,
        url: URL.createObjectURL(file)
      }))
    },
    removeImage (index) {
      if (this.imagePreviews[index]) {
        URL.revokeObjectURL(this.imagePreviews[index].url)
        this.imagePreviews.splice(index, 1)
        this.selectedFiles = this.imagePreviews.map(p => p.file)
      }
    },
    async handleUpload () {
      if (!this.canUpload || this.uploading) {
        return
      }

      this.uploading = true
      this.errorMessage = null
      this.uploadResults = null

      try {
        const formData = new FormData()
        this.imagePreviews.forEach((preview, index) => {
          formData.append(`images[${index}]`, preview.file)
        })

        const response = await this.$itemService.batchUploadImages(formData)
        console.log(response)
        // Handle response with statistics object
        if (response.statistics) {
          const stats = response.statistics
          this.uploadResults = {
            matched_count: stats.matched_images || 0,
            skipped_count: stats.skipped_no_match || 0,
            unmatched_files: stats.unmatched_filenames || []
          }

          if (stats.status === 'success') {
            this.$notify.success(this.$t('item.message.batch_upload_success'))
          } else {
            this.errorMessage = response.message || this.$t('item.message.batch_upload_error')
            this.$notify.error(this.errorMessage)
          }
        } else if (response.status === 'success' || response.status === 'error') {
          // Fallback for alternative response format
          this.uploadResults = {
            matched_count: response.matched_count || 0,
            skipped_count: response.skipped_count || 0,
            unmatched_files: response.unmatched_files || []
          }

          if (response.status === 'success') {
            this.$notify.success(this.$t('item.message.batch_upload_success'))
          } else {
            this.errorMessage = response.message || this.$t('item.message.batch_upload_error')
            this.$notify.error(this.errorMessage)
          }
        } else {
          // Legacy format support
          this.uploadResults = response
          this.$notify.success(this.$t('item.message.batch_upload_success'))
        }
      } catch (error) {
        console.error('Batch upload error:', error)
        const errorMsg = error.response?.data?.message || error.message || this.$t('item.message.batch_upload_error')
        this.errorMessage = errorMsg
        this.$notify.error(errorMsg)
      } finally {
        this.uploading = false
      }
    },
    handleClose () {
      this.imagePreviews.forEach(preview => {
        URL.revokeObjectURL(preview.url)
      })
      this.reset()
      this.$emit('close')
    },
    formatFileSize (bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
    }
  },
  beforeDestroy () {
    this.imagePreviews.forEach(preview => {
      URL.revokeObjectURL(preview.url)
    })
  }
}
</script>

<style scoped>
.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
